import { normalizeTimezone } from "@shared/utils/dates/timezone/normalizeTimezone";

type TimezoneObject = { name?: unknown };

type ResolveTimezoneOptions = {
  fallback: "browser" | "null";
};

const DEFAULT_OPTIONS: ResolveTimezoneOptions = {
  fallback: "browser",
};

const getBrowserTimezone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

const extractTimezoneName = (timezone: unknown): string | null => {
  if (typeof timezone === "string") {
    return timezone;
  }

  if (
    timezone &&
    typeof timezone === "object" &&
    "name" in timezone &&
    typeof (timezone as TimezoneObject).name === "string"
  ) {
    return (timezone as { name: string }).name;
  }

  return null;
};

/**
 * Resolves mixed timezone shapes to a normalized IANA timezone id.
 *
 * Use this when timezone input might be either a string (e.g. "America/Edmonton")
 * or an object with a `name` field (e.g. `{ name: "America/Edmonton" }`).
 *
 * Fallback behavior:
 * - `fallback: "null"` returns null when no timezone is present.
 * - `fallback: "browser"` returns `Intl.DateTimeFormat().resolvedOptions().timeZone`
 *   when no timezone is present. Use this only in client-only contexts
 *   (e.g. mobile or code guaranteed not to run during SSR).
 *
 * SSR-safe web usage:
 * `const tz = resolveTimezoneId(venue?.timezone, { fallback: "null" }) ?? useBrowserTimezone();`
 *
 * Note: This helper normalizes legacy timezone aliases via `normalizeTimezone()`.
 */
export const resolveTimezoneId = (
  timezone: unknown,
  options: ResolveTimezoneOptions = DEFAULT_OPTIONS
): string | null => {
  const timezoneName = extractTimezoneName(timezone);

  if (timezoneName) {
    return normalizeTimezone(timezoneName) ?? timezoneName;
  }

  if (options.fallback === "null") {
    return null;
  }

  return getBrowserTimezone();
};
